import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { logError } from "@/lib/logger";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    EnvelopeSimple,
    WhatsappLogo,
    PaperPlaneTilt,
    SkipForward,
    ArrowLeft,
    ArrowRight,
    CheckCircle,
    Warning,
} from "@phosphor-icons/react";

// Kept in sync with PersonDetailPanel's REACH_TEMPLATES — same wording so
// recruiters see identical output regardless of single vs bulk path.
const TEMPLATES = {
    intro: {
        label: "Job intro",
        subject: "Opportunity — {job_title} at {client_name}",
        body: `Hi {first_name},

Hope you're well! I'm reaching out about a {job_title} role at {client_name}. Based on your background at {current_title}, I think this could be a strong fit.

Would you be open to a quick 15-min chat this week?

— {recruiter_name}`,
    },
    followup: {
        label: "Screening follow-up",
        subject: "Following up — {job_title}",
        body: `Hi {first_name},

Just circling back on your application for {job_title} at {client_name}. Do you have 15 minutes this week for a quick screening call?

Best,
{recruiter_name}`,
    },
    interview: {
        label: "Interview slot ask",
        subject: "Interview slots — {job_title}",
        body: `Hi {first_name},

Great news — {client_name} would like to move you to the interview round for {job_title}. Please share 2-3 slots that work for you over the next few days.

— {recruiter_name}`,
    },
    offer_congrats: {
        label: "Congratulations on hire",
        subject: "Congrats on joining {client_name}!",
        body: `Hi {first_name},

Congratulations on the offer! It was a pleasure supporting your journey to {client_name} for {job_title}. Wishing you all the best.

— {recruiter_name}`,
    },
};

function interpolate(tpl, ctx) {
    return (tpl || "")
        .replaceAll("{name}", ctx.name || "")
        .replaceAll("{first_name}", ctx.first_name || "there")
        .replaceAll("{job_title}", ctx.job_title || "the role")
        .replaceAll("{client_name}", ctx.client_name || "our client")
        .replaceAll("{current_title}", ctx.current_title || "your current role")
        .replaceAll("{recruiter_name}", ctx.recruiter_name || "the recruiter");
}

function getRecruiterName() {
    try {
        const u = JSON.parse(localStorage.getItem("user") || "{}");
        return u?.name || "the recruiter";
    } catch { return "the recruiter"; }
}

export default function BulkReachOutModal({ open, candidateIds, onClose }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [idx, setIdx] = useState(0);
    const [channel, setChannel] = useState("email");         // email | whatsapp
    const [templateKey, setTemplateKey] = useState("intro");
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [opened, setOpened] = useState(() => new Set());   // candidate_ids opened
    const [skipped, setSkipped] = useState(() => new Set()); // candidate_ids skipped

    const recruiterName = useMemo(getRecruiterName, [open]);

    // Fetch enrich payload when modal opens.
    useEffect(() => {
        if (!open || !candidateIds?.length) return;
        setLoading(true);
        setIdx(0);
        setOpened(new Set());
        setSkipped(new Set());
        api.post("/candidates/bulk/reach-out-payload", { candidate_ids: candidateIds })
            .then((r) => {
                const list = r.data?.items || [];
                setItems(list);
                const dropped = r.data?.skipped || 0;
                if (dropped > 0) {
                    toast.info(`${dropped} candidate${dropped === 1 ? "" : "s"} dropped (dedup or access).`);
                }
            })
            .catch((e) => {
                logError("bulk reach-out enrich failed", e);
                toast.error(e?.response?.data?.detail || "Couldn't load candidates.");
            })
            .finally(() => setLoading(false));
    }, [open, candidateIds]);

    const cur = items[idx] || null;

    // Rebuild subject/body whenever cur or template changes.
    useEffect(() => {
        if (!cur) return;
        const t = TEMPLATES[templateKey] || TEMPLATES.intro;
        const ctx = { ...cur, recruiter_name: recruiterName };
        setSubject(interpolate(t.subject, ctx));
        setBody(interpolate(t.body, ctx));
    }, [cur, templateKey, recruiterName]);

    const missingChannel = cur && (channel === "email" ? !cur.has_email : !cur.has_phone);

    const openDraft = () => {
        if (!cur) return;
        if (channel === "whatsapp") {
            if (!cur.has_phone) return;
            const digits = String(cur.phone).replace(/[^0-9]/g, "");
            const msg = `${subject ? subject + "\n\n" : ""}${body}`;
            window.open(`https://wa.me/${digits}?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
        } else {
            if (!cur.has_email) return;
            window.open(
                `mailto:${cur.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
                "_blank",
                "noopener,noreferrer",
            );
        }
        setOpened((s) => { const n = new Set(s); n.add(cur.candidate_id); return n; });
        // Auto-advance to next candidate (if any).
        setTimeout(() => setIdx((i) => Math.min(i + 1, items.length - 1)), 200);
    };

    const skip = () => {
        if (!cur) return;
        setSkipped((s) => { const n = new Set(s); n.add(cur.candidate_id); return n; });
        setIdx((i) => Math.min(i + 1, items.length - 1));
    };
    const goPrev = () => setIdx((i) => Math.max(0, i - 1));
    const goNext = () => setIdx((i) => Math.min(items.length - 1, i + 1));

    const total = items.length;
    const progress = total ? Math.round((opened.size / total) * 100) : 0;
    const isDone = total > 0 && (opened.size + skipped.size >= total);

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent data-testid="bulk-reach-out-modal" className="max-w-2xl p-0 gap-0">
                <DialogHeader className="px-4 sm:px-6 py-3 border-b border-[#E5E5E5]">
                    <DialogTitle className="text-base font-black uppercase tracking-[0.15em]">
                        Bulk Reach-out · {opened.size + skipped.size} of {total}
                    </DialogTitle>
                </DialogHeader>

                {/* progress bar */}
                <div className="h-1 bg-[#F5F5F5]">
                    <div
                        data-testid="bulk-reach-progress"
                        className="h-full bg-cobalt transition-all"
                        style={{ width: `${progress}%` }}
                    />
                </div>

                {loading ? (
                    <div className="p-8 text-center text-sm text-[#A3A3A3]">Loading candidates…</div>
                ) : total === 0 ? (
                    <div className="p-8 text-center text-sm text-[#A3A3A3]">
                        No accessible candidates in the selection.
                    </div>
                ) : isDone ? (
                    <div className="p-8 text-center space-y-3">
                        <CheckCircle size={48} weight="fill" className="text-[#059669] mx-auto" />
                        <p className="font-black uppercase tracking-[0.15em] text-ink">
                            Queue complete
                        </p>
                        <p className="text-sm text-[#525252]">
                            {opened.size} draft{opened.size === 1 ? "" : "s"} opened · {skipped.size} skipped
                        </p>
                        <button
                            data-testid="bulk-reach-done"
                            onClick={onClose}
                            className="px-4 py-2 text-xs font-black uppercase tracking-wider bg-ink text-white"
                        >
                            Close
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="px-4 sm:px-6 py-4 space-y-3 max-h-[65vh] overflow-y-auto">
                            {/* Current candidate header */}
                            <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                    <p
                                        data-testid="bulk-reach-current-name"
                                        className="font-black text-ink truncate"
                                    >
                                        {cur?.name || "—"}
                                        {opened.has(cur?.candidate_id) && (
                                            <span className="ml-2 text-[9px] font-black uppercase tracking-wider bg-[#ECFDF5] text-[#059669] px-1.5 py-0.5">Opened</span>
                                        )}
                                        {skipped.has(cur?.candidate_id) && (
                                            <span className="ml-2 text-[9px] font-black uppercase tracking-wider bg-[#F5F5F5] text-[#525252] px-1.5 py-0.5">Skipped</span>
                                        )}
                                    </p>
                                    <p className="text-[10px] text-[#A3A3A3] truncate">
                                        {cur?.current_title || "—"} · {channel === "email" ? cur?.email : cur?.phone || "no phone"}
                                    </p>
                                    <p className="text-[10px] text-[#A3A3A3] truncate">
                                        For: {cur?.job_title || "—"} · {cur?.client_name || ""}
                                    </p>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <button
                                        type="button"
                                        onClick={goPrev}
                                        disabled={idx === 0}
                                        aria-label="Previous"
                                        className="p-1.5 border border-[#E5E5E5] hover:border-ink disabled:opacity-40"
                                    >
                                        <ArrowLeft size={12} weight="bold" />
                                    </button>
                                    <span className="text-[10px] font-black tracking-wider text-[#525252] w-16 text-center" data-testid="bulk-reach-pos">
                                        {idx + 1} / {total}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={goNext}
                                        disabled={idx === total - 1}
                                        aria-label="Next"
                                        className="p-1.5 border border-[#E5E5E5] hover:border-ink disabled:opacity-40"
                                    >
                                        <ArrowRight size={12} weight="bold" />
                                    </button>
                                </div>
                            </div>

                            {/* Channel switch */}
                            <div className="flex gap-1.5">
                                <button
                                    type="button"
                                    data-testid="bulk-reach-channel-email"
                                    onClick={() => setChannel("email")}
                                    className={`flex-1 px-3 py-2 text-[10px] font-black uppercase tracking-wider inline-flex items-center justify-center gap-1 ${
                                        channel === "email" ? "bg-ink text-white" : "border border-[#E5E5E5] text-[#525252]"
                                    }`}
                                >
                                    <EnvelopeSimple size={12} weight="bold" /> Email
                                </button>
                                <button
                                    type="button"
                                    data-testid="bulk-reach-channel-whatsapp"
                                    onClick={() => setChannel("whatsapp")}
                                    className={`flex-1 px-3 py-2 text-[10px] font-black uppercase tracking-wider inline-flex items-center justify-center gap-1 ${
                                        channel === "whatsapp" ? "bg-[#059669] text-white" : "border border-[#E5E5E5] text-[#525252]"
                                    }`}
                                >
                                    <WhatsappLogo size={12} weight="bold" /> WhatsApp
                                </button>
                            </div>

                            {/* Template picker */}
                            <div>
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-1.5">Template (applies to the whole queue)</p>
                                <div className="flex flex-wrap gap-1.5">
                                    {Object.entries(TEMPLATES).map(([k, t]) => (
                                        <button
                                            key={k}
                                            type="button"
                                            data-testid={`bulk-reach-template-${k}`}
                                            onClick={() => setTemplateKey(k)}
                                            className={`text-[10px] font-black uppercase tracking-wider px-2 py-1 border ${
                                                templateKey === k ? "bg-ink text-white border-ink" : "bg-white text-[#525252] border-[#E5E5E5] hover:border-ink"
                                            }`}
                                        >
                                            {t.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {channel === "email" && (
                                <div>
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-1.5">Subject (personalised per candidate)</p>
                                    <input
                                        data-testid="bulk-reach-subject"
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                        className="w-full px-3 py-2 text-sm bg-white border border-[#E5E5E5] focus:outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt"
                                    />
                                </div>
                            )}
                            <div>
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-1.5">
                                    Message · <span className="normal-case tracking-normal">variables auto-filled per candidate</span>
                                </p>
                                <textarea
                                    data-testid="bulk-reach-body"
                                    value={body}
                                    onChange={(e) => setBody(e.target.value)}
                                    rows={7}
                                    className="w-full px-3 py-2 text-sm bg-white border border-[#E5E5E5] focus:outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt resize-y font-mono"
                                />
                            </div>

                            {missingChannel && (
                                <div className="flex items-start gap-2 bg-[#FEF3C7] border border-[#F59E0B]/30 p-2.5 text-[11px] text-[#78350F]">
                                    <Warning size={14} weight="bold" className="shrink-0 mt-0.5" />
                                    <span>
                                        {channel === "email"
                                            ? "This candidate has no email on file — draft can't be opened."
                                            : "This candidate has no phone on file — WhatsApp draft can't be opened."}
                                        Skip to the next.
                                    </span>
                                </div>
                            )}
                        </div>

                        <DialogFooter className="px-4 sm:px-6 py-3 border-t border-[#E5E5E5] gap-2">
                            <button
                                type="button"
                                data-testid="bulk-reach-skip"
                                onClick={skip}
                                className="px-3 py-2 text-[10px] font-black uppercase tracking-wider border border-[#E5E5E5] text-[#525252] hover:border-ink inline-flex items-center gap-1"
                            >
                                <SkipForward size={12} weight="bold" /> Skip
                            </button>
                            <button
                                type="button"
                                data-testid="bulk-reach-open-draft"
                                onClick={openDraft}
                                disabled={missingChannel}
                                className={`px-3 py-2 text-[10px] font-black uppercase tracking-wider text-white inline-flex items-center gap-1 disabled:opacity-50 ${
                                    channel === "whatsapp" ? "bg-[#059669]" : "bg-cobalt"
                                }`}
                            >
                                {channel === "whatsapp" ? <WhatsappLogo size={12} weight="bold" /> : <PaperPlaneTilt size={12} weight="bold" />}
                                Open draft & next
                            </button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
